import { Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import {
  ArrowRight,
  CheckCircle,
  TrendingUp,
  MapPin,
  Laptop,
  DollarSign,
  Layers,
  BarChart3,
  Building2,
  Heart,
  Users,
  Target,
} from "lucide-react";
import SEO from "../components/SEO";
import StructuredData from "../components/StructuredData";
import MidPageCTA from "../components/MidPageCTA";
import SectionDivider from "../components/SectionDivider";

const GrowthScalability = () => {
  const outcomes = [
    "A clear, actionable growth roadmap tailored to your practice",
    "Operational systems that can handle increased patient volume without breaking",
    "Confidence to expand into new locations, services, or markets",
    "Diversified revenue streams that reduce reliance on any single payer",
    "Technology infrastructure that supports. not limits. your growth",
    "A team structure designed to scale with you, not against you",
    "Data-driven decision-making for every major growth investment",
  ];

  const whoIsFor = [
    { icon: Building2, label: "Practices ready to add a second or third location" },
    { icon: Heart, label: "Clinics experiencing growing pains from increased patient volume" },
    { icon: Users, label: "Healthcare startups planning their first major scaling phase" },
    { icon: TrendingUp, label: "Established practices looking to diversify services or revenue" },
  ];

  return (
    <div>
      <SEO
        title="Healthcare Practice Growth & Scalability Consulting"
        description="Ready to grow but worried your operations can't keep up? RNB Consulting Group helps healthcare practices build the systems, team structure, and strategy needed to scale without the chaos."
        keywords="healthcare practice growth consulting, scale medical practice, multi-location expansion healthcare, telehealth integration, healthcare revenue diversification, practice scalability, grow clinic operations"
      />
      <StructuredData data={[{
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        "itemListElement": [
          { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://www.rnbconsultinggroup.com" },
          { "@type": "ListItem", "position": 2, "name": "Services", "item": "https://www.rnbconsultinggroup.com/services" },
          { "@type": "ListItem", "position": 3, "name": "Growth & Scalability", "item": "https://www.rnbconsultinggroup.com/services/growth-scalability" }
        ]
      }]} />

      {/* Hero Section */}
      <section className="relative py-20 lg:py-28 bg-gradient-to-br from-rnb-navy via-rnb-navy to-rnb-blue overflow-hidden">
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-20 right-20 w-72 h-72 bg-rnb-teal/15 rounded-full blur-3xl" />
          <div className="absolute bottom-10 left-10 w-96 h-96 bg-rnb-blue/20 rounded-full blur-3xl" />
        </div>

        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="grid lg:grid-cols-2 gap-10 items-center">
            <div className="text-white">
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 backdrop-blur-sm border border-white/20 mb-8">
                <TrendingUp className="w-4 h-4 text-rnb-teal" />
                <span className="text-sm font-medium">Growth & Scalability Consulting</span>
              </div>

              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold leading-tight tracking-tight mb-6" data-testid="growth-hero-headline">
                Growth Should Be Exciting
                <span className="block mt-2 bg-gradient-to-r from-rnb-teal to-cyan-300 bg-clip-text text-transparent">
                  Not Terrifying
                </span>
              </h1>

              <p className="text-lg sm:text-xl text-white/80 leading-relaxed mb-8">
                You've built something worth scaling. But growth without the right systems creates more problems than it solves. We help you build the operational foundation so you can grow with confidence. not chaos.
              </p>

              <ul className="flex flex-wrap gap-x-6 gap-y-3 mb-10 text-white/90 text-sm sm:text-base">
                <li className="flex items-center gap-2"><CheckCircle className="w-5 h-5 text-rnb-teal flex-shrink-0" /> Scale with confidence</li>
                <li className="flex items-center gap-2"><CheckCircle className="w-5 h-5 text-rnb-teal flex-shrink-0" /> Expand strategically</li>
                <li className="flex items-center gap-2"><CheckCircle className="w-5 h-5 text-rnb-teal flex-shrink-0" /> Diversify revenue</li>
              </ul>

              <Link to="/consultation">
                <Button
                  size="lg"
                  className="bg-rnb-teal hover:bg-rnb-teal/90 text-white rounded-full px-8 py-6 text-lg font-semibold shadow-glow hover:shadow-glow-lg transition-all duration-300 hover:-translate-y-1"
                  data-testid="growth-hero-cta"
                >
                  Schedule a Free Consultation
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </Link>
            </div>

            <div className="relative">
              <img
                src="/images/healthcare/hospital-team-tablet.jpg"
                alt="Healthcare leadership team reviewing growth strategy on tablet"
                className="rounded-2xl shadow-2xl w-full h-56 sm:h-72 lg:h-auto object-cover border-2 border-white/10"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Problem Acknowledgment */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-rnb-navy mb-4">
              Sound Familiar?
            </h2>
            <p className="text-rnb-gray text-lg max-w-2xl mx-auto">
              These are the growing pains we help practices work through. before they become growing failures.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 gap-6">
            {[
              "You want to open a second location but your current one still feels like it's held together with tape",
              "Revenue is up but margins are flat. because growth is creating more overhead, not more profit",
              "You know telehealth could expand your reach, but you haven't had time to figure out the setup",
              "Every time you add staff or patients, something in the workflow breaks",
              "You're making big decisions based on gut feel instead of data because you don't have the right reporting",
              "You've been 'about to grow' for two years but operational fires keep pulling you back",
            ].map((problem, idx) => (
              <div key={problem.slice(0, 40)} className="flex items-start gap-3 p-4 rounded-xl bg-slate-50 border border-slate-100">
                <div className="w-6 h-6 rounded-full bg-orange-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <span className="text-orange-500 text-xs font-bold">{idx + 1}</span>
                </div>
                <p className="text-rnb-gray leading-relaxed">{problem}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How We Help */}
      <section className="py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-14">
            <h2 className="text-3xl sm:text-4xl font-bold text-rnb-navy mb-4">
              How We Help You Scale the Right Way
            </h2>
            <p className="text-rnb-gray text-lg max-w-2xl mx-auto">
              We don't just hand you a growth plan. we build the operational infrastructure to support it.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              { icon: Target, title: "Operational Readiness Assessment", desc: "Before you grow, we assess whether your current systems, team, and processes can handle it. and we fix the gaps that would cause problems at a larger scale." },
              { icon: MapPin, title: "Multi-Location Expansion Planning", desc: "Opening a new location is more than finding a lease. We help you plan the operational blueprint. staffing models, workflows, technology, and quality control across sites." },
              { icon: Laptop, title: "Telehealth & Virtual Care Setup", desc: "We help you integrate telehealth into your practice in a way that works for your patients, your providers, and your bottom line. not just check a box." },
              { icon: DollarSign, title: "Revenue Diversification Strategy", desc: "Relying on one payer or one service line is risky. We help you identify and plan for new revenue streams. from ancillary services to cash-pay offerings to new specialties." },
              { icon: Layers, title: "Scalable Systems Design", desc: "We build operational systems. scheduling, intake, documentation, handoffs. that work at 50 patients a day just as well as they work at 15." },
              { icon: BarChart3, title: "Growth Metrics & Decision Frameworks", desc: "We set up the dashboards, KPIs, and financial models you need to make data-driven growth decisions. so you're investing based on evidence, not hope." },
            ].map((item, idx) => (
              <Card key={item.title || item.name || item.label || idx} className="border-0 shadow-sm hover:shadow-md transition-all duration-300 hover:-translate-y-1">
                <CardContent className="p-7">
                  <div className="w-12 h-12 bg-rnb-light rounded-xl flex items-center justify-center mb-5">
                    <item.icon className="w-6 h-6 text-rnb-blue" />
                  </div>
                  <h3 className="text-lg font-bold text-rnb-navy mb-2">{item.title}</h3>
                  <p className="text-rnb-gray text-sm leading-relaxed">{item.desc}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Mid-page CTA */}
      <MidPageCTA variant={1} image="/images/healthcare/medical-team-meeting.png" />

      {/* Outcomes */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-rnb-navy mb-4">
              What You Can Expect
            </h2>
            <p className="text-rnb-gray text-lg max-w-2xl mx-auto">
              Practices we partner with don't just grow. they grow sustainably, with systems that keep things running smoothly at every stage.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 gap-4">
            {outcomes.map((outcome, idx) => (
              <div key={outcome.slice(0, 40)} className="flex items-start gap-3 p-4">
                <CheckCircle className="w-5 h-5 text-rnb-teal flex-shrink-0 mt-0.5" />
                <span className="text-rnb-navy font-medium">{outcome}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Who This Is For */}
      <section className="py-20 bg-slate-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-rnb-navy mb-4">
              Who This Is For
            </h2>
          </div>

          <div className="grid sm:grid-cols-2 gap-6">
            {whoIsFor.map((item, idx) => (
              <div key={item.title || item.name || item.label || idx} className="flex items-center gap-4 p-5 bg-white rounded-xl shadow-sm">
                <div className="w-11 h-11 bg-rnb-light rounded-xl flex items-center justify-center flex-shrink-0">
                  <item.icon className="w-5 h-5 text-rnb-blue" />
                </div>
                <span className="text-rnb-navy font-medium">{item.label}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why RNB */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-rnb-navy mb-4">
              Why Healthcare Practices Choose RNB
            </h2>
          </div>

          <div className="grid sm:grid-cols-2 gap-6">
            {[
              { icon: Heart, title: "Healthcare-Only Focus", desc: "We've seen what works and what doesn't in healthcare growth. Our playbooks are built from real experience with clinics, not MBA theory or tech startup advice." },
              { icon: Layers, title: "Systems Before Scale", desc: "We won't let you grow into a bigger mess. We make sure your foundation is solid before adding more patients, more staff, or more locations." },
              { icon: BarChart3, title: "Financial Clarity", desc: "Growth shouldn't be a leap of faith. We model the financials, project the costs, and help you understand the ROI before you commit to any major investment." },
              { icon: Users, title: "End-to-End Support", desc: "From strategic planning through implementation, we stay with you. We don't disappear after the plan is written. we help you execute it." },
            ].map((item, idx) => (
              <div key={item.title || item.name || item.label || idx} className="flex items-start gap-4 p-5">
                <div className="w-10 h-10 bg-rnb-teal/10 rounded-xl flex items-center justify-center flex-shrink-0">
                  <item.icon className="w-5 h-5 text-rnb-teal" />
                </div>
                <div>
                  <h3 className="font-bold text-rnb-navy mb-1">{item.title}</h3>
                  <p className="text-rnb-gray text-sm leading-relaxed">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20 bg-gradient-to-br from-rnb-navy to-rnb-blue relative overflow-hidden">
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-0 right-0 w-96 h-96 bg-rnb-teal/15 rounded-full blur-3xl" />
        </div>

        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-6">
            You've Built Something Worth Scaling. Let's Do It Right
          </h2>
          <p className="text-lg text-white/80 mb-10 max-w-2xl mx-auto">
            The practices that scale successfully aren't the ones that grow the fastest. they're the ones that grow the smartest. Let's build a plan that makes your next phase of growth your strongest.
          </p>
          <div className="flex flex-wrap gap-4 justify-center">
            <Link to="/consultation">
              <Button
                size="lg"
                className="bg-white text-rnb-navy hover:bg-white/90 rounded-full px-8 py-6 text-lg font-semibold shadow-lg hover:-translate-y-1 transition-all duration-300"
                data-testid="growth-bottom-cta"
              >
                Book a Free Consultation
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
            <Link to="/quote">
              <Button
                size="lg"
                variant="outline"
                className="border-2 border-white/30 text-white hover:bg-white/10 rounded-full px-8 py-6 text-lg font-semibold transition-all duration-300"
              >
                Get an Instant Quote
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default GrowthScalability;
